import { Button, Form, Input } from "antd"
import { useEffect, useState } from "react"

import { REACT_APP_API_URL } from "src/global/env.ts"
import { styled } from "styled-components"

/**
 * Organization name validation:
 * - Can only contain English letters, numbers, and common punctuation
 * - Cannot start or end with hyphen (-) or underscore (_)
 * Note: Leading and trailing spaces will be trimmed
 */
// from https://stackoverflow.com/questions/14313183/javascript-regex-how-do-i-check-if-the-string-is-ascii-only
// equivalent of python string.printable
const isPrintableASCII = (str: string) => /^[\x20-\x7E]*$/.test(str)
const startsWithRestrictedChar = (str: string) => /^[-_]/.test(str)
const endsWithRestrictedChar = (str: string) => /[-_]$/.test(str)

export const ORGANIZATION_NAME_MIN_LENGTH = 3
export const ORGANIZATION_NAME_MAX_LENGTH = 40

export const ORGANIZATION_NAME_VALIDATION_MESSAGES = {
  PRINTABLE_CHARS: "Organization name can only contain English letters, numbers, spaces, and common punctuation",
  STARTS_WITH_RESTRICTED: "Organization name cannot start with a hyphen or underscore",
  ENDS_WITH_RESTRICTED: "Organization name cannot end with a hyphen or underscore",
  MIN_LENGTH: `Organization name must be at least ${ORGANIZATION_NAME_MIN_LENGTH} characters`,
  MAX_LENGTH: `Organization name cannot exceed ${ORGANIZATION_NAME_MAX_LENGTH} characters`,
} as const

interface SignupFormProps {
  onSubmitSuccess: (email: string) => void
}

const StyledForm = styled(Form)`
  width: 100%;
`

const StyledButton = styled(Button)`
  width: 100%;
  box-shadow: none;
`

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error.gxError};
  margin-top: ${({ theme }) => theme.spacing.xxs};
  text-align: center;
`

const SignupForm = ({ onSubmitSuccess }: SignupFormProps) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false)

  const values = Form.useWatch([], form)

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmitDisabled(false))
      .catch(() => setIsSubmitDisabled(true))
  }, [form, values])

  const onSubmit = async () => {
    setLoading(true)
    setError("")
    const { email, organizationName } = form.getFieldsValue()

    try {
      const response = await fetch(`${REACT_APP_API_URL}/accounts/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ admin_email: email, organization_name: organizationName }),
      })

      if (!response.ok) {
        throw new Error("Failed to create account")
      }
      onSubmitSuccess(email)
    } catch {
      setError("There was an error creating your account. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <StyledForm size="large" form={form} layout="vertical">
        <Form.Item
          name="email"
          label="Your email"
          rules={[{ required: true, type: "email", message: "Please enter a valid email" }]}
        >
          <Input type="email" aria-label="Email" />
        </Form.Item>
        <Form.Item
          name="organizationName"
          label="Organization name"
          rules={[
            {
              required: true,
              min: ORGANIZATION_NAME_MIN_LENGTH,
              message: ORGANIZATION_NAME_VALIDATION_MESSAGES.MIN_LENGTH,
              transform: (value) => value?.trim(),
            },
            {
              max: ORGANIZATION_NAME_MAX_LENGTH,
              message: ORGANIZATION_NAME_VALIDATION_MESSAGES.MAX_LENGTH,
              transform: (value) => value?.trim(),
            },
            {
              validator: async (_, value) => {
                if (!value) return

                const trimmedValue = value.trim()
                if (!isPrintableASCII(trimmedValue)) {
                  throw new Error(ORGANIZATION_NAME_VALIDATION_MESSAGES.PRINTABLE_CHARS)
                }
                if (startsWithRestrictedChar(trimmedValue)) {
                  throw new Error(ORGANIZATION_NAME_VALIDATION_MESSAGES.STARTS_WITH_RESTRICTED)
                }
                if (endsWithRestrictedChar(trimmedValue)) {
                  throw new Error(ORGANIZATION_NAME_VALIDATION_MESSAGES.ENDS_WITH_RESTRICTED)
                }
              },
            },
          ]}
        >
          <Input aria-label="Organization name" />
        </Form.Item>
      </StyledForm>
      <StyledButton type="primary" size="large" onClick={onSubmit} disabled={isSubmitDisabled} loading={loading}>
        Sign up
      </StyledButton>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </>
  )
}

export default SignupForm
